import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminApi } from '../services/api';
import { ArrowLeft, Save, Copy, Check, Eye, EyeOff, Palette, Globe, Image, Bell, Phone } from 'lucide-react';

export default function AdminClientForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [form, setForm] = useState({
    nome: '', dominio: '', webhookUrl: '', logoUrl: '',
    corPrimaria: '#6366F1', maxSalas: 100,
    textoBoasVindas: 'Bem-vindo a sua consulta',
    textoBotaoEntrar: 'Entrar na Sala',
    exibirChat: true, exibirBlur: true,
    deviceCheckEnabled: true,
    chatEnabled: true,
    supportEnabled: false, supportPhone: '',
    hostJoinConfirmEnabled: false,
  });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  useEffect(() => {
    if (!isNew && id) {
      adminApi.getClient(id).then(data => {
        if (data && !data.error) setForm(prev => ({ ...prev, ...data }));
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [id, isNew]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.nome.trim()) return alert('Nome e obrigatorio');
    setSaving(true);
    try {
      if (isNew) {
        const result = await adminApi.createClient(form);
        if (result.apiKey) {
          setCreated(result);
        } else {
          alert(result.error || 'Erro ao criar');
        }
      } else {
        await adminApi.updateClient(id, form);
        alert('Cliente atualizado!');
      }
    } catch (err) {
      alert('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
    </div>
  );

  // Tela de credenciais apos criar
  if (created) return (
    <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 max-w-lg w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-emerald-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold">Cliente criado!</h2>
          <p className="text-gray-400 mt-2">Guarde as credenciais abaixo. O API Secret nao sera mostrado novamente.</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 uppercase">Nome</label>
            <p className="text-lg font-medium">{created.nome}</p>
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase">API Key</label>
            <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-4 py-3 mt-1">
              <code className="flex-1 text-sm text-emerald-400 break-all">{created.apiKey}</code>
              <button onClick={() => copyToClipboard(created.apiKey, 'key')}
                className="text-gray-400 hover:text-white">
                {copied === 'key' ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase">API Secret</label>
            <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-4 py-3 mt-1">
              <code className="flex-1 text-sm text-amber-400 break-all">
                {showSecret ? created.apiSecret : '•'.repeat(40)}
              </code>
              <button onClick={() => setShowSecret(!showSecret)} className="text-gray-400 hover:text-white">
                {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button onClick={() => copyToClipboard(created.apiSecret, 'secret')}
                className="text-gray-400 hover:text-white">
                {copied === 'secret' ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          <Link to="/admin" className="flex-1 text-center py-3 bg-gray-800 hover:bg-gray-700 rounded-xl transition-colors">
            Voltar ao Dashboard
          </Link>
          <Link to="/playground" className="flex-1 text-center py-3 bg-indigo-600 hover:bg-indigo-500 rounded-xl transition-colors">
            Testar no Playground
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/admin" className="text-gray-400 hover:text-white"><ArrowLeft size={20} /></Link>
          <h1 className="text-xl font-bold">{isNew ? 'Novo Cliente' : `Editar: ${form.nome}`}</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Info basica */}
        <section className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Globe size={18} /> Informacoes do Cliente
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Nome da empresa *</label>
              <input value={form.nome} onChange={(e) => handleChange('nome', e.target.value)}
                className="w-full bg-gray-800 rounded-lg px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="HelpGlobal" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Dominio</label>
              <input value={form.dominio} onChange={(e) => handleChange('dominio', e.target.value)}
                className="w-full bg-gray-800 rounded-lg px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="help.helpglobal.com" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Max salas simultaneas</label>
              <input type="number" value={form.maxSalas} onChange={(e) => handleChange('maxSalas', parseInt(e.target.value) || 100)}
                className="w-full bg-gray-800 rounded-lg px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1"><Bell size={14} /> Webhook URL</label>
              <input value={form.webhookUrl} onChange={(e) => handleChange('webhookUrl', e.target.value)}
                className="w-full bg-gray-800 rounded-lg px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://helpglobal.com/webhook" />
            </div>
          </div>
        </section>

        {/* Whitelabel / Branding */}
        <section className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Palette size={18} /> Whitelabel / Branding
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1"><Image size={14} /> URL do Logo</label>
              <input value={form.logoUrl} onChange={(e) => handleChange('logoUrl', e.target.value)}
                className="w-full bg-gray-800 rounded-lg px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://helpglobal.com/logo.png" />
              {form.logoUrl && (
                <div className="mt-2 bg-gray-800 rounded-lg p-3 flex items-center justify-center">
                  <img src={form.logoUrl} alt="Logo" className="h-12 object-contain"
                    onError={(e) => e.target.style.display = 'none'} />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Cor primaria</label>
              <div className="flex gap-2">
                <input type="color" value={form.corPrimaria} onChange={(e) => handleChange('corPrimaria', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer bg-transparent" />
                <input value={form.corPrimaria} onChange={(e) => handleChange('corPrimaria', e.target.value)}
                  className="flex-1 bg-gray-800 rounded-lg px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                  placeholder="#6366F1" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Texto de boas-vindas</label>
              <input value={form.textoBoasVindas} onChange={(e) => handleChange('textoBoasVindas', e.target.value)}
                className="w-full bg-gray-800 rounded-lg px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Bem-vindo a sua consulta" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Texto do botao entrar</label>
              <input value={form.textoBotaoEntrar} onChange={(e) => handleChange('textoBotaoEntrar', e.target.value)}
                className="w-full bg-gray-800 rounded-lg px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Entrar na Sala" />
            </div>
          </div>

          {/* Feature toggles */}
          <div className="mt-4 flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.exibirChat}
                onChange={(e) => handleChange('exibirChat', e.target.checked)}
                className="w-4 h-4 rounded bg-gray-700 text-indigo-600" />
              <span className="text-sm">Exibir Chat</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.exibirBlur}
                onChange={(e) => handleChange('exibirBlur', e.target.checked)}
                className="w-4 h-4 rounded bg-gray-700 text-indigo-600" />
              <span className="text-sm">Exibir Desfoque de Fundo</span>
            </label>
          </div>

          {/* Preview */}
          <div className="mt-6">
            <p className="text-sm text-gray-400 mb-2">Preview da tela de entrada:</p>
            <div className="bg-gray-800 rounded-xl p-6 max-w-sm mx-auto border border-gray-700">
              {form.logoUrl && <img src={form.logoUrl} alt="" className="h-10 mx-auto mb-3"
                onError={(e) => e.target.style.display = 'none'} />}
              <p className="text-center text-gray-300 text-sm mb-4">{form.textoBoasVindas || 'Bem-vindo'}</p>
              <div className="bg-gray-700 rounded-lg px-3 py-2 mb-3 text-gray-400 text-sm">Seu nome</div>
              <button className="w-full py-2.5 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: form.corPrimaria }}>
                {form.textoBotaoEntrar || 'Entrar na Sala'}
              </button>
            </div>
          </div>
        </section>

        {/* Tela de ativacao de dispositivos + Suporte */}
        <section className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Phone size={18} /> Experiencia de entrada do paciente
          </h2>

          {/* Device check toggle */}
          <div className="mb-5 pb-5 border-b border-gray-800">
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input type="checkbox" checked={form.deviceCheckEnabled}
                onChange={(e) => handleChange('deviceCheckEnabled', e.target.checked)}
                className="w-4 h-4 rounded bg-gray-700 text-indigo-600" />
              <span className="text-sm font-medium">Forcar ativacao de camera e microfone antes de entrar</span>
            </label>
            <p className="text-xs text-gray-500 ml-6">
              Quando ativado, o paciente ve uma tela de teste de dispositivos com indicadores
              visuais (permitido/bloqueado) antes de ser conectado a consulta.
              Quando desativado, o paciente entra direto e depende das permissoes automaticas do navegador.
            </p>
          </div>

          {/* Chat toggle */}
          <div className="mb-5 pb-5 border-b border-gray-800">
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input type="checkbox" checked={form.chatEnabled}
                onChange={(e) => handleChange('chatEnabled', e.target.checked)}
                className="w-4 h-4 rounded bg-gray-700 text-indigo-600" />
              <span className="text-sm font-medium">Habilitar chat de mensagens na sala</span>
            </label>
            <p className="text-xs text-gray-500 ml-6">
              Quando desativado, o botao de chat nao aparece nem para o medico nem para o paciente,
              independente do parametro chat=on na URL. Use se a sua plataforma ja tem chat proprio
              ou se a politica interna proibe mensagens de texto durante o atendimento.
            </p>
          </div>

          {/* Confirmacao de entrada pro medico */}
          <div className="mb-5 pb-5 border-b border-gray-800">
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input type="checkbox" checked={form.hostJoinConfirmEnabled}
                onChange={(e) => handleChange('hostJoinConfirmEnabled', e.target.checked)}
                className="w-4 h-4 rounded bg-gray-700 text-indigo-600" />
              <span className="text-sm font-medium">Exigir confirmacao do medico antes de entrar</span>
            </label>
            <p className="text-xs text-gray-500 ml-6">
              Quando ativado, o medico com permissoes de camera/microfone ja concedidas ve
              uma tela com botao "Entrar" antes de cair na sala, em vez de entrar direto.
              Paciente nao e afetado - sempre entra direto se as permissoes estao ok.
              Se o medico ainda nao tem permissao, ele continua vendo a tela de ativacao de
              dispositivos normalmente (nesse caso o "Entrar" nao aparece - o clique em
              "Permitir acesso" ja serve como confirmacao).
            </p>
          </div>

          {/* Support toggle + phone */}
          <p className="text-sm text-gray-400 mb-4">
            Quando o paciente tem dificuldade pra ativar camera ou microfone, o sistema pode
            exibir um botao com o telefone de suporte. So aparece se o forcar ativacao estiver ligado.
          </p>
          <label className={`flex items-center gap-2 mb-4 ${form.deviceCheckEnabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
            <input type="checkbox" checked={form.supportEnabled}
              onChange={(e) => handleChange('supportEnabled', e.target.checked)}
              disabled={!form.deviceCheckEnabled}
              className="w-4 h-4 rounded bg-gray-700 text-indigo-600" />
            <span className="text-sm">Exibir contato de suporte na tela de ativacao de dispositivos</span>
          </label>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Telefone do suporte</label>
            <input value={form.supportPhone || ''}
              onChange={(e) => handleChange('supportPhone', e.target.value)}
              className="w-full bg-gray-800 rounded-lg px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="+55 11 99999-9999"
              disabled={!form.supportEnabled || !form.deviceCheckEnabled} />
            <p className="text-xs text-gray-500 mt-1">
              Formato livre. O sistema gera link `tel:` e `wa.me/` removendo caracteres nao numericos.
            </p>
          </div>
        </section>

        {/* Salvar */}
        <button onClick={handleSave} disabled={saving}
          className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
          <Save size={18} />
          {saving ? 'Salvando...' : (isNew ? 'Criar Cliente' : 'Salvar Alteracoes')}
        </button>
      </main>
    </div>
  );
}
